<script lang="ts" setup>
    import { reactive } from 'vue';
    import '../../../css/choice.css';
    import * as choiceApi from '../../lib/ChoiceApi';
    import { ModalStyles, popAlert } from '../../lib/Notification';
    import ActionButtonV2 from '../../components/ActionButtonV2.vue';
    import { ActionButtonStyle } from '../../lib/Types/Types';
    import TextInput from '../../components/TextInput.vue';
    import { SupportPinValidationResult } from '../../lib/Api/CustomerSupport';

    const props = defineProps<{
        isBangUser: boolean
    }>();

    const pageData = reactive<{
        pin?: number,
        pinToValidate?: string,
        validationResult?: SupportPinValidationResult
    }>({
        pin: undefined,
        pinToValidate: undefined,
        validationResult: undefined
    });

    async function generateSupportPin() {
        const result = await choiceApi.customerSupport.generateSupportPin();
        if (result === undefined) {
            popAlert("An error occurred while generating a pin. Please try again.", "Pin Generation", ModalStyles.info);
            return;
        }
        pageData.pin = result;
    }

    async function validateSupportPin() {
        pageData.validationResult = undefined;
        if (isNaN(Number(pageData.pinToValidate)) || (pageData.pinToValidate && pageData.pinToValidate?.length !== 6)) {
            popAlert("An invalid pin was provided.", "Pin Validation", ModalStyles.info);
            return;
        }
        const result = await choiceApi.customerSupport.validateSupportPin(Number(pageData.pinToValidate));
        pageData.validationResult = result;
        pageData.pinToValidate = undefined;
    }

</script>

<style scoped>
    #patientAppointments {
        width: 98vw;
        height: 98vh;
        overflow-x: hidden;
    }
</style>

<template>
    <div id="patientAppointments" class="bg-white px-2">
        <div v-if="!props.isBangUser" class="py-2">
            <h5 class="text-primary pb-3 border-bottom">Support Pin</h5>
            <div class="mt-1 mb-5">
                {{ `Provide this number to the iSalus Support team when requested. This pin assists us in validating your identity when entering a case via Phone or Email. You may also use the` }}
                <b>{{ `Log a Support Ticket` }}</b>
                {{ `screen for streamlined validation.` }}
            </div>
            <div v-if=pageData.pin class="d-flex justify-content-center fw-bold fs-3 mb-2">
                {{ pageData.pin }}
            </div>
            <div class="d-flex justify-content-center">
                <ActionButtonV2
                    :action="ActionButtonStyle.NoIcon" 
                    :text="pageData.pin ? 'Regenerate' : 'Generate'"
                    spinText="Generating..."
                    :performAction="generateSupportPin"
                />
            </div>
        </div>
        <div v-else class="py-2">
            <h5 class="text-primary pb-3 border-bottom">Support Pin Validation</h5>
            <div class="mt-1 mb-5">
                {{ `Enter the Support PIN provided to you by the user.` }}
            </div>
            <div class="d-flex justify-content-center gap-2">
                <div style="width: 15%">
                    <TextInput
                        v-model:textValue="pageData.pinToValidate"
                    />
                </div>
                <ActionButtonV2
                    :action="ActionButtonStyle.NoIcon" 
                    :disabled="pageData.pinToValidate === undefined"
                    :text="'Validate'"
                    spinText="Validating..."
                    :performAction="validateSupportPin" 
                />
            </div>
            <div v-if=pageData.validationResult class="d-flex justify-content-center mt-3">
                <div v-if="!pageData.validationResult.success" class="alert alert-danger">
                    {{ pageData.validationResult.message }}
                </div>
                <div v-else class="alert alert-success">
                    {{ pageData.validationResult.data }}
                </div>
            </div>
        </div>
    </div>
</template>

